import React, { useMemo } from 'react';
import { UploadOutlined } from '@ant-design/icons';
import { Input, Upload, Button, Divider, Form, Spin } from 'antd';
import ApiService from '@services/apiService';
import AIIcon from '@assets/AI_with_notes.webp';
import ObjectiveAIMedia from './ObjectiveAIMedia';
import ObjectiveAIDropdown from './ObjectiveAIDropdown';

interface ObjectiveMediaFormProps {
  media: ObjectiveMedia[];
  onObjectiveAIMedia: () => void;
  objectiveItems: ObjectiveAIMedia[];
  setItems: React.Dispatch<React.SetStateAction<ObjectiveAIMedia[]>>;
  isLoading: boolean;
  onSelectModel: React.Dispatch<React.SetStateAction<string>>;
}

interface HandleAddItemProps {
  add: (defaultValue?: any, insertIndex?: number | undefined) => void;
  item: ObjectiveAIMedia;
  idx: number;
}

const buttonAIStyles = {
  border: 'none',
  padding: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'transparent',
} as React.CSSProperties;

const ObjectiveMediaForm: React.FC<ObjectiveMediaFormProps> = ({
  media,
  onObjectiveAIMedia,
  objectiveItems,
  setItems,
  isLoading,
  onSelectModel,
}) => {
  const initialValues = useMemo(
    () =>
      media.map((m) => ({
        ...m,
        file: m.file
          ? [
              {
                uid: m.file.id,
                name: m.file.pid || m.file.url,
                status: 'done',
                ...m.file,
              },
            ]
          : [],
      })),
    [media]
  );

  const normFile = (e: any) => {
    // console.log('Upload event:', e);
    if (Array.isArray(e)) {
      return e;
    }
    return e && e.fileList;
  };

  const onRemoveItem = (idx: number) => {
    setItems((prevItems) => prevItems.filter((_, i) => i !== idx));
  };

  const onAddItem = ({ add, item, idx }: HandleAddItemProps) => {
    add(item);
    onRemoveItem(idx);
  };

  return (
    <>
      <h3>Objective Media</h3>

      <Form.List name="media" initialValue={initialValues}>
        {(fields, { add, remove }, { errors }) => (
          <>
            {fields.map(({ key, name, ...restField }) => (
              <div key={key}>
                <Form.Item name={`${name}.id`} {...restField} hidden>
                  <Input />
                </Form.Item>
                <Form.Item
                  name={[name, 'time']}
                  label="Time (in minutes)"
                  {...restField}
                  rules={[
                    {
                      required: true,
                      message: 'This field is required!',
                    },
                  ]}
                >
                  <Input type="number" min={1} />
                </Form.Item>
                <Form.Item
                  name={[name, 'description']}
                  label="Description"
                  {...restField}
                  rules={[
                    {
                      required: true,
                      message: 'This field is required!',
                    },
                  ]}
                >
                  <Input.TextArea rows={2} />
                </Form.Item>
                <Form.Item {...restField} name={[name, 'url']} label="Url">
                  <Input />
                </Form.Item>
                <h4>OR</h4>
                <Form.Item
                  isListField
                  name={[name, 'file']}
                  label="File"
                  valuePropName="fileList"
                  getValueFromEvent={normFile}
                >
                  <Upload
                    name="file"
                    action={ApiService.getUrl('/files')}
                    headers={ApiService.getUploadHeaders()}
                    listType="picture"
                    // onChange={e => onUploadChange(e, index)}
                  >
                    <Button>
                      <UploadOutlined /> Click to upload
                    </Button>
                  </Upload>
                </Form.Item>
                <Button danger onClick={() => remove(name)}>
                  Delete media
                </Button>
                <Divider />
              </div>
            ))}
            <div
              style={{
                display: 'flex',
                gap: '1rem',
                alignContent: 'center',
                alignItems: 'center',
              }}
            >
              <Button type="primary" onClick={() => add()}>
                Add new media
              </Button>
              <ObjectiveAIDropdown onSelectModel={onSelectModel} />
              <Button
                type="default"
                disabled={isLoading}
                style={buttonAIStyles}
                onClick={onObjectiveAIMedia}
              >
                {isLoading ? (
                  <Spin />
                ) : (
                  <img
                    style={{
                      height: '33px',
                      width: '33px',
                    }}
                    src={AIIcon}
                  />
                )}
              </Button>
            </div>
            {objectiveItems?.map((item, idx) => {
              const isNotLastItem = idx < objectiveItems.length - 1;
              return (
                <>
                  <ObjectiveAIMedia
                    key={`${item.url}/(index)`}
                    url={item.url}
                    time={item.time}
                    description={item.description}
                    onAdd={() => onAddItem({ add, item, idx })}
                    onRemove={() => onRemoveItem(idx)}
                  />
                  {isNotLastItem && <Divider />}
                </>
              );
            })}
          </>
        )}
      </Form.List>
    </>
  );
};

export default ObjectiveMediaForm;
